import { Box, Link } from '@mui/joy';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useUserPermissions } from 'auth/hooks';
import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { AccessPermissions, FileUploadType, NotificationPosition } from 'core/enums';
import { useFilesActions } from 'core/hooks';
import { IConfirmation } from 'core/types';
import { goTo } from 'core/utils';
import { useCorporateCostingProfileActions } from 'corporate/hooks';
import { IPricingProfile } from 'pricing/types';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { mapFormToImportAttributes } from 'quotes/components/VendorQuoteDetails/Tabs/Attributes/mappers';
import { VendorQuoteAttachToType, VendorQuoteDuplicateOutcome } from 'quotes/enums';
import {
  TImportQuoteActions,
  useImportExtractDataChecker,
  useImportQuoteActions,
  useImportQuoteChecker,
  useValidateVendorQuoteBeforeImport,
  useValidateVendorQuoteBeforeImportAction,
} from 'quotes/hooks';
import { useQuoteActions } from 'quotes/hooks';
import { mapVendorLocationToImportPayload } from 'quotes/mappers';
import { oldQuotePartListPath } from 'quotes/routes/routePaths';
import {
  IExtractDataProgress,
  IImportAttributesPayload,
  IImportedQuoteData,
  IImportVendorQuoteForm,
  IImportVendorQuotePayload,
  IQuoteQuoteAssociationForm,
  IVendorContactSignal,
  IVendorMappingSignal,
  IVendorQuote,
  IVendorQuoteNumberSignal,
  IVendorQuoteRevisionSignal,
} from 'quotes/types';
import { Color } from 'theme/enums';

import { getImportPromptPayload, resolveExtractedQuoteField } from './utils';

interface IProps {
  currentProjectId?: number;
  currentJobId?: number;
  currentQuoteId?: number;
  pricingProfile?: IPricingProfile;
  setQuoteQuoteAssociation?: Dispatch<SetStateAction<IQuoteQuoteAssociationForm>>;
  notificationPosition?: NotificationPosition;
  refetchQueriesKeys?: string[];
  excludeJobAssignments?: VendorQuoteAttachToType[];
  onImportComplete?: () => void;
  isUpdatingCurrentQuote?: boolean;
  withoutConfirmation?: boolean;
}

export const useImportVendorQuote = ({
  currentProjectId,
  currentJobId,
  currentQuoteId,
  pricingProfile,
  setQuoteQuoteAssociation,
  notificationPosition,
  refetchQueriesKeys,
  excludeJobAssignments = [],
  onImportComplete,
  withoutConfirmation = false,
  isUpdatingCurrentQuote = false,
}: IProps) => {
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const [isOpenImportVendorQuoteForm, setIsOpenImportVendorQuoteForm] = useState(false);
  const [attachedTo, setAttachedTo] = useState<VendorQuoteAttachToType | null>(null);
  const [partImportId, setPartImportId] = useState<number | null>(null);
  // `partImportId` flips the moment a new file is uploaded, while `importedQuoteData` and
  // `vendorContact` keep describing the PREVIOUS import until the new extraction lands. Consumers
  // that must reason about the import currently on screen key off this stamp instead, so the id and
  // the data it describes always come from the same import.
  const [extractedImportId, setExtractedImportId] = useState<number | null>(null);
  const [enabledExtractData, setEnabledExtractData] = useState(false);
  const [importedQuoteData, setImportedQuoteData] = useState<IImportedQuoteData | null>(null);
  const [vendorContact, setVendorContact] = useState<IVendorContactSignal | null | undefined>(null);
  const [vendorMapping, setVendorMapping] = useState<IVendorMappingSignal | null>(null);
  const [vendorQuoteNumber, setVendorQuoteNumber] = useState<IVendorQuoteNumberSignal | null>(null);
  const [vendorQuoteRevision, setVendorQuoteRevision] = useState<IVendorQuoteRevisionSignal | null>(
    null
  );
  // A failed finalize is retryable: re-posting the SAME payload completes the quote the failed
  // attempt created instead of creating a second one. The headless auto-import has no Confirm of
  // its own to retry with, so this hands it back to the contact prompt.
  const [isFinalizeFailed, setIsFinalizeFailed] = useState(false);
  const [enabledValidateVendorQuoteBeforeImport, setEnabledValidateVendorQuoteBeforeImport] =
    useState(false);
  const [enabledImportStatus, setEnabledImportStatus] = useState(false);
  const [confirmationOption, setConfirmationOption] = useState<IConfirmation | null>(null);
  const [importVendorQuotePayload, setImportVendorQuotePayload] =
    useState<IImportVendorQuotePayload | null>(null);

  // Fire `start` at most once per import id — survives the form remounting / the drawer re-opening.
  const startedImportRef = useRef<number | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Bridges the async validate→confirm detour between capture and payload assembly.
  const pendingAttributesRef = useRef<IAttributeForm | null>(null);

  // The attribute-free payload the gated wizard already dedup-checked, kept in a ref because the
  // final Confirm merges attributes into it in the same tick it is read. Stamped with the
  // extraction it was built from (extractedImportId — the id that flips together with the data,
  // unlike partImportId): the delayed close lets a second upload arrive while it is still
  // populated, and that upload must never be imported with the previous one's validated payload.
  const validatedImportRef = useRef<{
    importId: number | null;
    attachTo: VendorQuoteAttachToType;
    payload: IImportVendorQuotePayload;
  } | null>(null);

  // A pending delayed close must not fire after unmount.
  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  const { fileUpload } = useFilesActions({ position: notificationPosition });
  const { createVendorQuoteImport, importQuoteToJob, importQuoteToProject, importQuoteToQuote } =
    // Default the import mutations to a visible position: the auto-import path renders no form, so
    // a FORM-positioned loading/error is invisible (matches the extraction poll's DRAWER default).
    useImportQuoteActions({ position: notificationPosition ?? NotificationPosition.DRAWER });
  const { importVendorQuoteToCostingProfile } = useCorporateCostingProfileActions({
    position: notificationPosition,
  });

  const { refetch } = useValidateVendorQuoteBeforeImport(
    importVendorQuotePayload,
    enabledValidateVendorQuoteBeforeImport,
    ({ record }) => afterValidateImportVendorQuote(record)
  );
  const { validateVendorQuoteBeforeImport } = useValidateVendorQuoteBeforeImportAction({
    position: notificationPosition,
  });
  const { attachQuoteToProject, attachQuoteToQuote } = useQuoteActions();
  const { hasPermission } = useUserPermissions();

  const onExtractDataSuccess = ({
    quoteData,
    vendorContact,
    vendorMapping,
    vendorQuoteNumber,
    vendorQuoteRevision,
  }: IExtractDataProgress) => {
    setEnabledExtractData(false);
    setIsOpenImportVendorQuoteForm(true);
    setExtractedImportId(partImportId);
    setImportedQuoteData(quoteData);
    // Keep the tri-state intact: explicit `null` = re-import (auto-submit), a missing/undefined
    // field = unresolved (show the contact-only form). Do NOT coalesce `undefined` to `null`, or a
    // dropped/stripped field would wrongly trigger the headless re-import path.
    setVendorContact(vendorContact);
    // Unlike the tri-state above, an absent key carries no meaning here — a BE that predates the
    // feature simply sends none, and `null` is exactly "behave as before".
    setVendorMapping(vendorMapping ?? null);
    setVendorQuoteNumber(vendorQuoteNumber ?? null);
    setVendorQuoteRevision(vendorQuoteRevision ?? null);
  };
  useImportExtractDataChecker({
    criteria: { partImport: partImportId },
    enabled: enabledExtractData,
    onSuccess: onExtractDataSuccess,
    position: notificationPosition,
  });

  const onImportStatusSuccess = () => {
    setEnabledImportStatus(false);
    setImportedQuoteData(null);
    setVendorContact(null);
    setVendorMapping(null);
    setVendorQuoteNumber(null);
    setVendorQuoteRevision(null);
    setIsFinalizeFailed(false);

    // The import mutates the quote itself (FileNet status, vendor snapshot,
    // dates), so the details query must refresh even when the caller only
    // refetches part lists — else the chip/retry button lag until a reload.
    queryClient.invalidateQueries('quote');
    refetchQueriesKeys && queryClient.refetchQueries(refetchQueriesKeys);
    onImportComplete && onImportComplete();
  };
  useImportQuoteChecker({
    criteria: { partImport: partImportId },
    enabled: enabledImportStatus,
    onSuccess: onImportStatusSuccess,
    position: notificationPosition,
  });

  const onUploadVendorQuote = async (file: File, quoteId?: number) => {
    if (!file) {
      return;
    }

    // A new file is a new import: the previous one's failure must not keep its retry prompt up.
    setIsFinalizeFailed(false);

    // A new upload supersedes any pending delayed close from a previous import.
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    const { data } = await fileUpload(file, { fileType: FileUploadType.VENDOR_QUOTE_IMPORT });
    const fileId = data[0]?.id;

    const { id } = await createVendorQuoteImport({
      file: fileId,
      quote: quoteId,
    });

    setPartImportId(id);
    setEnabledExtractData(true);
  };

  const onImportVendorQuoteImpl = async (
    data: IImportVendorQuoteForm,
    attributes?: IAttributeForm | null
  ) => {
    pendingAttributesRef.current = attributes ?? null;

    // Guard: `start` must fire exactly once per import. The headless auto-submit can re-trigger — a
    // late extract-progress response re-opens the drawer and resets the form's local guard — which
    // otherwise creates the quote twice ("Quote has been already created").
    if (startedImportRef.current === partImportId) {
      return;
    }
    startedImportRef.current = partImportId;

    // Retry after a failed finalize: re-post directly, as the wizard's Confirm does. The dedup
    // check would now match the quote the failed attempt created and offer it as "existing".
    // A re-import only carries a vendor when the prompt asked for one (no usable mapping): its
    // check would match the quote being imported into, and the use-existing branch it would take
    // closes the prompt before the finalize can fail retryably.
    if (isFinalizeFailed || (isUpdatingCurrentQuote && data?.vendorLocation)) {
      await finalizeWithoutDuplicateCheck(data);

      return;
    }

    setAttachedTo(data.attachTo);
    setImportVendorQuotePayload(getPayloadByType(data));

    // Every other import path (new or re-import) runs the dedup check first: the validate query
    // fires on the committed re-render (enabled flips + the query key changes to the fresh
    // payload), then afterValidateImportVendorQuote decides import-new vs reuse-existing.
    setEnabledValidateVendorQuoteBeforeImport(true);

    if (enabledValidateVendorQuoteBeforeImport && importVendorQuotePayload) {
      refetch();

      setEnabledValidateVendorQuoteBeforeImport(false);
    }
  };

  // The payload is passed explicitly and the payload state left alone, so the declarative validate
  // query is not re-keyed into firing behind this call.
  const finalizeWithoutDuplicateCheck = async (data: IImportVendorQuoteForm) => {
    setAttachedTo(data.attachTo);

    try {
      await importVendorQuote(data.attachTo, getPayloadByType(data));
    } catch {
      startedImportRef.current = null;
      setIsFinalizeFailed(true);

      return;
    }

    onCloseImportVendorQuoteForm();
    setEnabledImportStatus(true);
  };

  // Expose a stable identity so the form's auto-submit effect (which lists `onImport` in its deps)
  // stops re-running on every render. The ref always points at the latest closure, so state stays
  // fresh; the startedImportRef guard still enforces one `start` per import. No behavior change.
  const onImportVendorQuoteRef = useRef(onImportVendorQuoteImpl);
  onImportVendorQuoteRef.current = onImportVendorQuoteImpl;
  const onImportVendorQuote = useCallback(
    (data: IImportVendorQuoteForm, attributes?: IAttributeForm | null) =>
      onImportVendorQuoteRef.current(data, attributes),
    []
  );

  const getImportAttributes = (): IImportAttributesPayload | null => {
    // The strict attributeSchema guarantees a scope, so present attributes are never empty —
    // null only means the cohort had no Attributes step at all.
    const attributes = pendingAttributesRef.current;

    if (!attributes) {
      return null;
    }

    return mapFormToImportAttributes(attributes, {
      canChooseSpaStatus: hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA),
      canEnableSelectableSpa: hasPermission(AccessPermissions.SELECTABLE_SPA),
    });
  };

  const getPayloadByType = (data: IImportVendorQuoteForm): IImportVendorQuotePayload => {
    const importAttributes = getImportAttributes();

    const payload = {
      quote: {
        ...(isUpdatingCurrentQuote && { id: currentQuoteId }),
        projectId: data?.project?.id,
        jobName: data?.jobName,
        contactId: data?.contact?.id,
        attachTo: data?.attachTo,
        startDate: importedQuoteData?.startDate,
        validUntil: importedQuoteData?.validUntil
          ? importedQuoteData.validUntil
          : dayjs().add(1, 'month').format(SERVER_FORMAT_DATE_LONG),
        externalNumber: resolveExtractedQuoteField(
          data?.externalNumber,
          importedQuoteData?.externalNumber
        ),
        externalProjectName: data?.externalProjectName,
        revisionNumber: resolveExtractedQuoteField(
          data?.revisionNumber,
          importedQuoteData?.revisionNumber
        ),
        bomId: currentJobId,
        ...(importAttributes ?? {}),
      },
      partImport: partImportId,
      mergeDuplicates: 1,
      // Every finalize path is built here, so declaring prompt support once covers all four
      // endpoints — including the headless contact-only submit, which shows no vendor picker.
      ...getImportPromptPayload(),
      // Sibling of the quote bag, never inside it — `quote[...]` is mass-assigned onto the quote.
      ...mapVendorLocationToImportPayload(data?.vendorLocation),
    };

    switch (data.attachTo) {
      case VendorQuoteAttachToType.QUOTE:
        return {
          ...payload,
          association: {
            quoteId: currentQuoteId,
          },
        };
      case VendorQuoteAttachToType.PROJECT:
        return {
          ...payload,
          association: {
            projectId: data?.project?.id,
          },
        };
      case VendorQuoteAttachToType.COSTING_PROFILE:
        return {
          ...payload,
          association: {
            discountStructureId: pricingProfile?.vendorCostStructureId,
          },
        };
      case VendorQuoteAttachToType.JOB:
      default:
        return {
          ...payload,
          ...{
            quote: {
              ...payload.quote,
              bomId: currentJobId,
            },
          },
        };
    }
  };

  // Reusing an existing vendor quote means ATTACHING it, an operation that only exists on the
  // customer-quote and project surfaces. A JOB import with no existing-quote context has nothing to
  // attach to — `importQuoteToJob` without `quote.id` is a create — so reusing there would silently
  // produce a second copy of the very quote the dialog is pointing at.
  const isJobImportWithoutQuoteContext = (attachTo: VendorQuoteAttachToType) =>
    VendorQuoteAttachToType.JOB === attachTo && !isUpdatingCurrentQuote;

  // Resolves true when the flow ended at the dialog (the existing quote was reused, or opened), and
  // false when the user canceled or the attach failed — the gated wizard branches on that decision
  // instead of just firing the dialog and walking away. It ALWAYS settles: an awaiting caller (the
  // contact step's Next, the pre-wizard check) would otherwise hang forever.
  const confirmUseExistingVendorQuote = (
    existingVendorQuote: IVendorQuote,
    attachTo: VendorQuoteAttachToType,
    payload?: IImportVendorQuotePayload
  ) =>
    new Promise<boolean>((resolve) => {
      const existingVendorQuotePath = oldQuotePartListPath(existingVendorQuote?.id);

      const confirmationMessage = (
        <Trans
          i18nKey="messageConfirmExistingVendorQuote"
          components={{
            wrapper: <Box></Box>,
            linkWrapper: <Link href={existingVendorQuotePath}></Link>,
          }}
        />
      );

      const canAttachToExisting = !isJobImportWithoutQuoteContext(attachTo);

      const onViewExisting = () => {
        setConfirmationOption(null);
        onCloseImportVendorQuoteForm();

        // Nothing is imported and the flow is over, so settle as "handled" — the gated caller must
        // not open the wizard behind the navigation.
        resolve(true);

        goTo(existingVendorQuotePath);
      };

      const onAttachToExisting = async () => {
        setConfirmationOption(null);

        try {
          await importExistingVendorQuote(existingVendorQuote, attachTo, payload);
        } catch {
          // useHandleErrors surfaced the failure. Settle as "not reused" and leave the form/wizard
          // on screen (nothing was closed yet) so the user can retry.
          startedImportRef.current = null;
          resolve(false);

          return;
        }

        // Closed only once the attach succeeded, so a rejection above can't strand the user in
        // front of a drawer that is already sliding out.
        onCloseImportVendorQuoteForm();

        refetchQueriesKeys && queryClient.refetchQueries(refetchQueriesKeys);

        resolve(true);
      };

      setConfirmationOption({
        title: t('titleInfo'),
        message: confirmationMessage,
        confirmBtnColor: Color.SUCCESS,
        confirmBtnLabel: canAttachToExisting
          ? t('buttonLabelUseExistingVendorQuote')
          : t('buttonLabelViewExistingVendorQuote'),
        onConfirm: canAttachToExisting ? onAttachToExisting : onViewExisting,
        onCancel: () => {
          setConfirmationOption(null);
          // Re-arm the once-per-import guard: it is set on entry to the submit, so leaving it armed
          // after a cancel dead-ends the form's Save button (#869eae5u5).
          startedImportRef.current = null;

          resolve(false);
        },
      });
    });

  const afterValidateImportVendorQuote = async (existingVendorQuote: IVendorQuote) => {
    setEnabledValidateVendorQuoteBeforeImport(false);

    if (!existingVendorQuote) {
      try {
        await importVendorQuote(attachedTo);
      } catch {
        // useHandleErrors surfaced it. Re-arm the once-per-import guard so the very same payload
        // can be posted again — without it the guard swallows the next Confirm and the button reads
        // as dead. Nothing is closed and no import-status poll is started, so the prompt stays on
        // screen with the values still in it; the headless path gets one via isFinalizeFailed.
        startedImportRef.current = null;
        setIsFinalizeFailed(true);

        return;
      }

      onCloseImportVendorQuoteForm();
      setEnabledImportStatus(true);

      return;
    }

    if (withoutConfirmation) {
      onCloseImportVendorQuoteForm();
      await importExistingVendorQuote(existingVendorQuote, attachedTo);
      setEnabledImportStatus(true);

      return;
    }

    await confirmUseExistingVendorQuote(existingVendorQuote, attachedTo);
  };

  // Gated (Attributes) new imports dedup-check BEFORE the wizard opens, and again whenever the
  // vendor contact is (re-)picked on the contact step, so attributes are never typed for a quote
  // that turns out to be a duplicate — the use-existing path discards them.
  const resolveVendorQuoteDuplicateImpl = async (
    data: IImportVendorQuoteForm
  ): Promise<VendorQuoteDuplicateOutcome> => {
    // The validated payload must be attribute-free: it is also what the use-existing path reuses,
    // and the final Confirm merges the captured attributes into it.
    pendingAttributesRef.current = null;

    const payload = getPayloadByType(data);

    // No `setAttachedTo`/`setImportVendorQuotePayload` here: every consumer on this path is handed
    // the payload explicitly, and writing the payload state would re-key the still-mounted
    // declarative validate query, phantom-firing a second validate (and so a second import).
    validatedImportRef.current = { importId: extractedImportId, attachTo: data.attachTo, payload };

    // A re-import's lookup would match the very quote being imported into. The payload above is
    // still recorded: the wizard's Confirm finalizes with it.
    if (isUpdatingCurrentQuote) {
      return VendorQuoteDuplicateOutcome.NONE;
    }

    let existingVendorQuote: IVendorQuote | null = null;

    try {
      const { record } = await validateVendorQuoteBeforeImport(payload);

      existingVendorQuote = record ?? null;
    } catch {
      // The check is advisory and useHandleErrors already surfaced the failure — let the user carry
      // on instead of stranding them in front of a wizard that never opens.
      return VendorQuoteDuplicateOutcome.NONE;
    }

    if (!existingVendorQuote) {
      return VendorQuoteDuplicateOutcome.NONE;
    }

    // The payload is threaded explicitly: this continuation resumes in the pre-commit closure, so
    // the `importVendorQuotePayload` state importExistingVendorQuote falls back to is still stale.
    const isReused = await confirmUseExistingVendorQuote(
      existingVendorQuote,
      data.attachTo,
      payload
    );

    return isReused ? VendorQuoteDuplicateOutcome.REUSED : VendorQuoteDuplicateOutcome.CANCELED;
  };

  // Final Confirm on the gated Attributes step: the contact was already dedup-checked on the step
  // before, so fire the import straight away rather than validating a second time. Resolves false
  // when the import did not happen, which keeps the wizard on the step with the typed attributes.
  const startVendorQuoteImportImpl = async (
    attributes?: IAttributeForm | null,
    // The wizard's own finalize mutations, bound to its drawer's notification provider — the ones
    // above report at the caller's position, which the open wizard cannot show.
    importActions?: TImportQuoteActions
  ) => {
    const validatedImport = validatedImportRef.current;

    // This import already fired — a late extract-progress response can re-open the drawer on top of
    // a completed import. There is nothing to retry, so let the wizard close as usual rather than
    // parking the user on a step whose Confirm can never do anything.
    if (startedImportRef.current === partImportId) {
      return;
    }

    // Fail closed on a missing or stale stamp: the validated payload must describe the extraction
    // this wizard renders, and no newer upload may be in flight (partImportId flips first).
    if (
      !validatedImport ||
      validatedImport.importId !== extractedImportId ||
      extractedImportId !== partImportId
    ) {
      return false;
    }

    startedImportRef.current = partImportId;
    pendingAttributesRef.current = attributes ?? null;

    const importAttributes = getImportAttributes();
    const payload: IImportVendorQuotePayload = {
      ...validatedImport.payload,
      quote: {
        ...validatedImport.payload.quote,
        ...(importAttributes ?? {}),
      },
    };

    try {
      await importVendorQuote(validatedImport.attachTo, payload, importActions);
    } catch {
      // useHandleErrors surfaced it — in the drawer, where the user still is. Re-arm the
      // once-per-import guard and report failure so the wizard stays on the Attributes step and
      // Confirm retries with the values still typed in.
      startedImportRef.current = null;

      return false;
    }

    onCloseImportVendorQuoteForm();
    setEnabledImportStatus(true);

    return true;
  };

  // Stable identities, same reason as onImportVendorQuote above: the form's pre-wizard effect and
  // the wizard's memoized step callbacks list these in their dependencies.
  const resolveVendorQuoteDuplicateRef = useRef(resolveVendorQuoteDuplicateImpl);
  resolveVendorQuoteDuplicateRef.current = resolveVendorQuoteDuplicateImpl;
  const resolveVendorQuoteDuplicate = useCallback(
    (data: IImportVendorQuoteForm) => resolveVendorQuoteDuplicateRef.current(data),
    []
  );

  const startVendorQuoteImportRef = useRef(startVendorQuoteImportImpl);
  startVendorQuoteImportRef.current = startVendorQuoteImportImpl;
  const startVendorQuoteImport = useCallback(
    (attributes?: IAttributeForm | null, importActions?: TImportQuoteActions) =>
      startVendorQuoteImportRef.current(attributes, importActions),
    []
  );

  const importVendorQuote = async (
    attachTo: VendorQuoteAttachToType,
    payload: IImportVendorQuotePayload = importVendorQuotePayload,
    // Which notification position the finalize reports to: the caller's by default, the wizard's
    // own drawer when it hands its actions over.
    actions: TImportQuoteActions = { importQuoteToJob, importQuoteToProject, importQuoteToQuote }
  ) => {
    switch (attachTo) {
      case VendorQuoteAttachToType.QUOTE:
        return actions.importQuoteToQuote(payload, {
          onSuccess: ({ quoteQuoteAssociation }) =>
            setQuoteQuoteAssociation &&
            setQuoteQuoteAssociation({
              quoteId: quoteQuoteAssociation.quoteId,
              vendorQuotes: [quoteQuoteAssociation.attachedQuoteId],
            }),
        } as any);
      case VendorQuoteAttachToType.PROJECT:
        return actions.importQuoteToProject(payload, {
          onSuccess: ({ projectQuoteAssociation }) =>
            setQuoteQuoteAssociation &&
            setQuoteQuoteAssociation({
              quoteId: currentQuoteId,
              vendorQuotes: [projectQuoteAssociation.quoteId],
            }),
        } as any);
      case VendorQuoteAttachToType.COSTING_PROFILE:
        // Not switchable to `actions`: the wizard never mounts for a costing-profile import
        // (ImportVendorQuoteForm gates the Attributes step off), so this only ever runs headless.
        return importVendorQuoteToCostingProfile(payload as any);
      case VendorQuoteAttachToType.JOB:
      default:
        return actions.importQuoteToJob(payload, {
          onSuccess: ({ quote }) =>
            setQuoteQuoteAssociation &&
            setQuoteQuoteAssociation({
              quoteId: currentQuoteId,
              vendorQuotes: [quote.id],
            }),
        } as any);
    }
  };

  const importExistingVendorQuote = async (
    existingVendorQuote: IVendorQuote,
    attachTo: VendorQuoteAttachToType,
    payload: IImportVendorQuotePayload = importVendorQuotePayload
  ) => {
    if (excludeJobAssignments.includes(attachTo)) {
      return;
    }

    // No attributes here on purpose: reusing an existing vendor quote must not overwrite the
    // attributes already saved on it, so the wizard never reaches the capture step on this path.
    switch (attachTo) {
      case VendorQuoteAttachToType.QUOTE: {
        await attachQuoteToQuote({
          quoteQuoteAssociations: [
            {
              quoteId: currentQuoteId,
              attachedQuoteId: existingVendorQuote?.id,
            },
          ],
        });
        break;
      }
      case VendorQuoteAttachToType.PROJECT: {
        await attachQuoteToProject({
          projectQuoteAssociations: [
            {
              quoteId: existingVendorQuote?.id,
              projectId: currentProjectId,
            },
          ],
        });
        break;
      }
      case VendorQuoteAttachToType.JOB:
        // Belt and braces for the silent-duplicate trap described on the predicate above: only the
        // re-import surface (payload carrying the quote id) may merge into the existing quote here.
        // Everything else has no attach semantics and must not create a second copy.
        if (isJobImportWithoutQuoteContext(attachTo) || !payload?.quote?.id) {
          break;
        }

        await importQuoteToJob(payload);
        break;
      default:
        break;
    }
  };

  const onCloseImportVendorQuoteForm = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    // Delay the close so the drawer slides out smoothly before the open/form state resets (project
    // convention: 1s before a form closes). Covers every close path, since they all route through
    // here. setEnabledImportStatus stays immediate (below), so its loading shows during slide-out.
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpenImportVendorQuoteForm(false);
      setImportedQuoteData(null);
      setVendorContact(null);
      setVendorMapping(null);
      setVendorQuoteNumber(null);
      setVendorQuoteRevision(null);
      setIsFinalizeFailed(false);
      closeTimeoutRef.current = null;
    }, 1000);
  };

  return {
    importedQuoteData,
    extractedImportId,
    vendorContact,
    vendorMapping,
    vendorQuoteNumber,
    vendorQuoteRevision,
    isFinalizeFailed,
    isOpenImportVendorQuoteForm,
    setIsOpenImportVendorQuoteForm,
    onCloseImportVendorQuoteForm,
    confirmationOption,
    onUploadVendorQuote,
    onImportVendorQuote,
    resolveVendorQuoteDuplicate,
    startVendorQuoteImport,
  };
};
